"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, FormStatus, SubmitButton } from "@/components/ui/Form";
import { TabHeader } from "./TabHeader";
import styles from "./CUserAccounts.module.css";

/**
 * ProfileTabPassword — zmiana hasła zalogowanego user'a.
 *
 * **Backend:** `POST /api/iiapi/user/change-password` ->
 * `IIAPI_CONTROLLER::IAPI_userUpdatePass`. Body: `{old_pass, new_pass, new_ppass}`.
 *
 * **Walidacja:** RHF + Zod (FORMS.md standard). Schema sprawdza wymagalność
 * pól + match haseł client-side. Server-side errors mapowane per-pole przez
 * `setError(name, {type: 'server'})` gdy backend zwróci `errors: {...}` (code 1010).
 */
const passwordSchema = z
  .object({
    old_pass: z.string().min(1, "Podaj aktualne hasło."),
    new_pass: z.string().min(1, "Podaj nowe hasło."),
    new_ppass: z.string().min(1, "Powtórz nowe hasło."),
  })
  .refine((v) => v.new_pass === v.new_ppass, {
    message: "Nowe hasła nie zgadzają się.",
    path: ["new_ppass"],
  });

type PasswordValues = z.infer<typeof passwordSchema>;

export function ProfileTabPassword() {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    mode: "onTouched",
    defaultValues: { old_pass: "", new_pass: "", new_ppass: "" },
  });
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (values: PasswordValues) => {
    setTopError(null);
    setSuccess(null);

    try {
      const res = await fetch("/api/iiapi/user/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
      } | null;

      if (!res.ok) {
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof PasswordValues, {
                type: "server",
                message: msg,
              });
            }
          }
          return;
        }
        setTopError(body?.message ?? "Nie udało się zmienić hasła.");
        return;
      }

      setSuccess(body?.message ?? "Hasło zostało zmienione.");
      reset({ old_pass: "", new_pass: "", new_ppass: "" });
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <TabHeader icon="lock" title="Zmiana hasła" />
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>

      <Field label="Aktualne hasło" required error={errors.old_pass?.message}>
        <Input
          type="password"
          autoComplete="current-password"
          disabled={isSubmitting}
          {...register("old_pass")}
        />
      </Field>

      <Field label="Nowe hasło" required error={errors.new_pass?.message}>
        <Input
          type="password"
          autoComplete="new-password"
          disabled={isSubmitting}
          {...register("new_pass")}
        />
      </Field>

      <Field label="Powtórz nowe hasło" required error={errors.new_ppass?.message}>
        <Input
          type="password"
          autoComplete="new-password"
          disabled={isSubmitting}
          {...register("new_ppass")}
        />
      </Field>

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      <div className={styles.profileFormActions}>
        <SubmitButton loading={isSubmitting}>Zmień hasło</SubmitButton>
      </div>
      </form>
    </>
  );
}
